import { memo, ReactElement, useEffect } from 'react';

import MenuItem from '@mui/material/MenuItem';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import useCourses from '../../dataHooks/useCourses';
import Course from '../../types/Course';
import GratiSystemError from '../common/GratiSystemError';
import Hourglass from '../common/Hourglass';

export type CourseSelectorProps = {
  value: string;
  handleChange: (course: Course) => void;
  disabled?: boolean;
  isRequired?: boolean;
  shortName?: boolean;
  helperText?: string;
  sx?: SxProps<Theme>;
};

const CourseSelector = memo(function CourseSelector(props: CourseSelectorProps): ReactElement {
  const { value, handleChange, disabled, helperText, isRequired, shortName, sx } = props;
  const { courses, isCoursesLoading, coursesError } = useCourses({});

  useEffect(() => {
    return () => {
      // enables the react-firebase-hook to do proper unloading.
    };
  }, [courses, isCoursesLoading, coursesError]);

  if (isCoursesLoading) {
    return <Hourglass />;
  }

  if (coursesError) {
    return <GratiSystemError error={coursesError} />;
  }

  if (!courses?.length) {
    return <Typography>No courses available</Typography>;
  }

  return (
    <TextField
      id="course"
      label="Course"
      required={isRequired}
      fullWidth
      disabled={disabled}
      helperText={helperText}
      select
      aria-label="Course"
      aria-required={isRequired}
      value={value}
      onChange={({ target }) => {
        const course = courses.find((course: Course) => course.itemId === target.value);
        if (course) {
          handleChange(course);
        }
      }}
      sx={sx}
    >
      {courses.map((course) => (
        <MenuItem key={course.itemId} value={course.itemId}>
          {shortName ? course.shortName : course.name}
        </MenuItem>
      ))}
    </TextField>
  );
});

export default CourseSelector;
